import { createContext, useEffect, useState } from 'react';
import { PublicKey } from '@solana/web3.js';
import { getSolanaConnection } from '../solana/connection';
import { decodeGameState } from '../solana/decoders/gameState';
import { fetchGlobalConfig } from '../solana/fetchers/globalConfig';
import { fetchCurrentReserveVault } from '../solana/fetchers/currentReserveVault';
import { fetchGlobalDumpVault } from '../solana/fetchers/globalDumpVault';
import { subscribeToCurrentReserveVault } from '../solana/subscriptions/currentReserveVault';
import { subscribeToGlobalConfig } from '../solana/subscriptions/globalConfig';
import { subscribeToGlobalDumpVault } from '../solana/subscriptions/globalDumpVault';

const EMPTY_PUBLIC_KEY = PublicKey.default.toBase58();

const initialState = {
  loading: {
    globalConfig: true,
    dumpVault: true,
    currentGameState: true,
    currentReserveVault: true,
  },
  error: {
    globalConfig: '',
    dumpVault: '',
    currentGameState: '',
    currentReserveVault: '',
  },
  subscribed: {
    globalConfig: false,
    dumpVault: false,
    currentGameState: false,
    currentReserveVault: false,
  },
  lastUpdated: {
    globalConfig: null,
    dumpVault: null,
    currentGameState: null,
    currentReserveVault: null,
  },
  globalConfig: null,
  globalConfigPubkey: '',
  dumpVault: null,
  dumpVaultPubkey: '',
  currentGameState: null,
  currentGamePubkey: '',
  currentReserveVault: null,
  currentReserveVaultPubkey: '',
};

export const SolanaLiveContext = createContext(undefined);

function normalizeActiveGamePubkey(value) {
  if (!value || value === EMPTY_PUBLIC_KEY) {
    return '';
  }

  return value;
}

function updateSectionState(previousState, section, updates) {
  return {
    ...previousState,
    ...updates,
    loading: {
      ...previousState.loading,
      ...(updates.loading || {}),
    },
    error: {
      ...previousState.error,
      ...(updates.error || {}),
    },
    subscribed: {
      ...previousState.subscribed,
      ...(updates.subscribed || {}),
    },
    lastUpdated: {
      ...previousState.lastUpdated,
      ...(updates.lastUpdated || {}),
    },
  };
}

export function SolanaLiveProvider({ children }) {
  const [state, setState] = useState(initialState);

  useEffect(() => {
    const connection = getSolanaConnection();
    let disposed = false;
    let currentGamePubkey = '';
    let currentGameSubscriptionId = null;
    let currentGameVersion = 0;
    let currentReserveVaultPubkey = '';
    let currentReserveVaultSubscriptionId = null;
    let currentReserveVaultVersion = 0;

    const clearCurrentGameSubscription = async () => {
      if (currentGameSubscriptionId === null) {
        return;
      }

      const subscriptionId = currentGameSubscriptionId;
      currentGameSubscriptionId = null;
      await connection.removeAccountChangeListener(subscriptionId);
    };

    const clearCurrentReserveVaultSubscription = async () => {
      if (currentReserveVaultSubscriptionId === null) {
        return;
      }

      const unsubscribe = currentReserveVaultSubscriptionId;
      currentReserveVaultSubscriptionId = null;
      await unsubscribe();
    };

    const setSectionError = (section, message) => {
      if (disposed) {
        return;
      }

      setState((previousState) =>
        updateSectionState(previousState, section, {
          loading: { [section]: false },
          error: { [section]: message },
        })
      );
    };

    const setGlobalConfigState = (result, isSubscribed) => {
      const now = Date.now();

      setState((previousState) =>
        updateSectionState(previousState, 'globalConfig', {
          globalConfig: result.data,
          globalConfigPubkey: result.publicKey,
          loading: { globalConfig: false },
          error: { globalConfig: '' },
          subscribed: { globalConfig: isSubscribed },
          lastUpdated: { globalConfig: now },
        })
      );

      return handleCurrentGameChange(result.data.active_game);
    };

    const setDumpVaultState = (result, isSubscribed) => {
      setState((previousState) =>
        updateSectionState(previousState, 'dumpVault', {
          dumpVault: {
            lamports: result.lamports,
            data: result.data,
          },
          dumpVaultPubkey: result.publicKey,
          loading: { dumpVault: false },
          error: { dumpVault: '' },
          subscribed: { dumpVault: isSubscribed },
          lastUpdated: { dumpVault: result.updatedAt || Date.now() },
        })
      );
    };

    const setCurrentGameStateState = (activeGamePubkey, data, isSubscribed, updatedAt) => {
      setState((previousState) =>
        updateSectionState(previousState, 'currentGameState', {
          currentGamePubkey: activeGamePubkey,
          currentGameState: data,
          loading: { currentGameState: false },
          error: { currentGameState: '' },
          subscribed: { currentGameState: isSubscribed },
          lastUpdated: { currentGameState: updatedAt || Date.now() },
        })
      );

      return handleCurrentReserveVaultChange(data?.reserve_vault);
    };

    const setCurrentReserveVaultState = (result, isSubscribed) => {
      setState((previousState) =>
        updateSectionState(previousState, 'currentReserveVault', {
          currentReserveVault: {
            lamports: result.lamports,
            data: result.data,
          },
          currentReserveVaultPubkey: result.publicKey,
          loading: { currentReserveVault: false },
          error: { currentReserveVault: '' },
          subscribed: { currentReserveVault: isSubscribed },
          lastUpdated: { currentReserveVault: result.updatedAt || Date.now() },
        })
      );
    };

    const handleCurrentReserveVaultChange = async (nextReserveVaultPubkey) => {
      const normalizedReserveVaultPubkey = normalizeActiveGamePubkey(nextReserveVaultPubkey);

      if (normalizedReserveVaultPubkey === currentReserveVaultPubkey) {
        return;
      }

      currentReserveVaultVersion += 1;
      const version = currentReserveVaultVersion;
      currentReserveVaultPubkey = normalizedReserveVaultPubkey;

      try {
        await clearCurrentReserveVaultSubscription();

        if (!currentReserveVaultPubkey) {
          setCurrentReserveVaultState(
            {
              publicKey: '',
              lamports: null,
              data: null,
              updatedAt: Date.now(),
            },
            false
          );
          return;
        }

        setState((previousState) =>
          updateSectionState(previousState, 'currentReserveVault', {
            currentReserveVaultPubkey,
            loading: { currentReserveVault: true },
            error: { currentReserveVault: '' },
            subscribed: { currentReserveVault: false },
          })
        );

        const result = await fetchCurrentReserveVault(currentReserveVaultPubkey);

        if (disposed || version !== currentReserveVaultVersion) {
          return;
        }

        setCurrentReserveVaultState({ ...result, updatedAt: Date.now() }, true);

        currentReserveVaultSubscriptionId = subscribeToCurrentReserveVault(
          currentReserveVaultPubkey,
          (updatedResult) => {
            if (!disposed) {
              setCurrentReserveVaultState(updatedResult, true);
            }
          },
          (error) => {
            setSectionError(
              'currentReserveVault',
              error.message || 'Current reserve vault update failed.'
            );
          }
        );
      } catch (error) {
        if (!disposed && version === currentReserveVaultVersion) {
          setSectionError(
            'currentReserveVault',
            error.message || 'Unable to load current reserve vault.'
          );
        }
      }
    };

    const handleCurrentGameChange = async (nextActiveGamePubkey) => {
      const normalizedActiveGamePubkey = normalizeActiveGamePubkey(nextActiveGamePubkey);

      if (normalizedActiveGamePubkey === currentGamePubkey) {
        return;
      }

      currentGameVersion += 1;
      const version = currentGameVersion;
      currentGamePubkey = normalizedActiveGamePubkey;

      try {
        await clearCurrentGameSubscription();

        if (!currentGamePubkey) {
          await handleCurrentReserveVaultChange('');
          setCurrentGameStateState('', null, false, Date.now());
          return;
        }

        setState((previousState) =>
          updateSectionState(previousState, 'currentGameState', {
            currentGamePubkey,
            loading: { currentGameState: true },
            error: { currentGameState: '' },
            subscribed: { currentGameState: false },
          })
        );

        const publicKey = new PublicKey(currentGamePubkey);
        const accountInfo = await connection.getAccountInfo(publicKey, 'confirmed');

        if (disposed || version !== currentGameVersion) {
          return;
        }

        if (!accountInfo) {
          throw new Error('Current gameState account was not found.');
        }

        setCurrentGameStateState(currentGamePubkey, decodeGameState(accountInfo.data), true, Date.now());

        currentGameSubscriptionId = connection.onAccountChange(
          publicKey,
          (updatedAccountInfo) => {
            try {
              if (disposed) {
                return;
              }

              setCurrentGameStateState(
                currentGamePubkey,
                decodeGameState(updatedAccountInfo.data),
                true,
                Date.now()
              );
            } catch (error) {
              setSectionError('currentGameState', error.message || 'Current gameState update failed.');
            }
          },
          'confirmed'
        );
      } catch (error) {
        if (!disposed && version === currentGameVersion) {
          setSectionError('currentGameState', error.message || 'Unable to load current gameState.');
        }
      }
    };

    fetchGlobalConfig()
      .then((result) => {
        if (!disposed) {
          setGlobalConfigState(result, false);
        }
      })
      .catch((error) => {
        setSectionError('globalConfig', error.message || 'Unable to load global_config.');
      });

    fetchGlobalDumpVault()
      .then((result) => {
        if (!disposed) {
          setDumpVaultState({ ...result, updatedAt: Date.now() }, false);
        }
      })
      .catch((error) => {
        setSectionError('dumpVault', error.message || 'Unable to load global_dump_vault.');
      });

    const unsubscribeFromGlobalConfig = subscribeToGlobalConfig(
      (result) => {
        if (!disposed) {
          setGlobalConfigState(result, true);
        }
      },
      (error) => {
        setSectionError('globalConfig', error.message || 'global_config subscription failed.');
      }
    );

    setState((previousState) =>
      updateSectionState(previousState, 'globalConfig', {
        subscribed: { globalConfig: true },
      })
    );

    const unsubscribeFromDumpVault = subscribeToGlobalDumpVault(
      (result) => {
        if (!disposed) {
          setDumpVaultState(result, true);
        }
      },
      (error) => {
        setSectionError('dumpVault', error.message || 'global_dump_vault subscription failed.');
      }
    );

    setState((previousState) =>
      updateSectionState(previousState, 'dumpVault', {
        subscribed: { dumpVault: true },
      })
    );

    return () => {
      disposed = true;
      unsubscribeFromGlobalConfig();
      unsubscribeFromDumpVault();
      clearCurrentReserveVaultSubscription().catch(() => {});
      clearCurrentGameSubscription().catch(() => {});
    };
  }, []);

  return <SolanaLiveContext.Provider value={state}>{children}</SolanaLiveContext.Provider>;
}
